import { TimeUnit } from './enum/TimeUnit';

// Time conversion constants (in milliseconds)
const _ONE_MILLISECOND = 1;
const ONE_SECOND_IN_MILLISECONDS = 1000;
const ONE_MINUTE_IN_MILLISECONDS = ONE_SECOND_IN_MILLISECONDS * 60;
const ONE_HOUR_IN_MILLISECONDS = ONE_MINUTE_IN_MILLISECONDS * 60;
const ONE_DAY_IN_MILLISECONDS = ONE_HOUR_IN_MILLISECONDS * 24;
const ONE_YEAR_IN_MILLISECONDS = ONE_DAY_IN_MILLISECONDS * 365;

// Derived time constants
const TWO_YEARS_IN_MILLISECONDS = ONE_YEAR_IN_MILLISECONDS * 2;
const HALF_A_YEAR_IN_MILLISECONDS = ONE_YEAR_IN_MILLISECONDS / 2;

// Note: This appears to be a legacy constant that should be reviewed
// as 3000ms is actually 3 seconds, not 10 minutes
const TEN_MINUTES = 3000;

/**
 * Represents a time window resolution configuration
 */
export type Resolution = {
  /** The unit of time for the window */
  windowUnit: TimeUnit;
  /** The size/duration of the window in the specified unit */
  windowSize: number;
};

/**
 * A smart time window utility that automatically determines the optimal
 * time resolution based on a given duration.
 *
 * This class maps time durations to appropriate window units and sizes
 * for data aggregation and visualization purposes.
 *
 * @example
 * ```typescript
 * const smartWindow = new SmartTimeWindow(ONE_HOUR_IN_MILLISECONDS, 2.0);
 * const resolution = smartWindow.getResolution();
 * console.log(resolution); // { windowUnit: TimeUnit.Minute, windowSize: 4 } (doubled)
 * ```
 */
export class SmartTimeWindow {
  /**
   * Maps duration thresholds to their optimal resolutions
   * The keys represent minimum durations in milliseconds
   */
  private readonly diffMap: Record<number, Resolution> = {};
  /**
   * Creates a new SmartTimeWindow instance
   * @param duration - The duration in milliseconds to determine resolution for
   * @param resolutionMultiplier - Multiplier for the resolution (1.0 = default, 0.5 = half resolution, 2.0 = double resolution)
   * @throws Error if duration is negative or resolutionMultiplier is not positive
   */
  constructor(
    public readonly duration: number,
    private readonly resolutionMultiplier: number = 1
  ) {
    if (duration < 0) {
      throw new Error('Duration must be non-negative');
    }
    if (resolutionMultiplier <= 0) {
      throw new Error('Resolution multiplier must be positive');
    }
    this.setup();
  }

  /**
   * Configuration for time window resolutions
   * Each entry maps a minimum duration threshold to its optimal resolution
   */
  private static readonly RESOLUTION_CONFIG: Array<{
    thresholdMs: number;
    resolution: Resolution;
    description?: string;
  }> = [
    {
      thresholdMs: TWO_YEARS_IN_MILLISECONDS,
      resolution: { windowUnit: TimeUnit.Day, windowSize: 4 },
      description: '2+ years: 4-day windows',
    },
    {
      thresholdMs: ONE_YEAR_IN_MILLISECONDS,
      resolution: { windowUnit: TimeUnit.Day, windowSize: 2 },
      description: '1+ year: 2-day windows',
    },
    {
      thresholdMs: HALF_A_YEAR_IN_MILLISECONDS,
      resolution: { windowUnit: TimeUnit.Day, windowSize: 1 },
      description: '6+ months: 1-day windows',
    },
    {
      thresholdMs: ONE_YEAR_IN_MILLISECONDS / 4,
      resolution: { windowUnit: TimeUnit.Hour, windowSize: 12 },
      description: '3+ months: 12-hour windows',
    },
    {
      thresholdMs: ONE_YEAR_IN_MILLISECONDS / 8,
      resolution: { windowUnit: TimeUnit.Hour, windowSize: 8 },
      description: '6+ weeks: 8-hour windows',
    },
    {
      thresholdMs: ONE_YEAR_IN_MILLISECONDS / 16,
      resolution: { windowUnit: TimeUnit.Hour, windowSize: 4 },
      description: '3+ weeks: 4-hour windows',
    },
    {
      thresholdMs: ONE_YEAR_IN_MILLISECONDS / 32,
      resolution: { windowUnit: TimeUnit.Hour, windowSize: 2 },
      description: '11+ days: 2-hour windows',
    },
    {
      thresholdMs: ONE_YEAR_IN_MILLISECONDS / 64,
      resolution: { windowUnit: TimeUnit.Minute, windowSize: 30 },
      description: '5+ days: 30-minute windows',
    },
    {
      thresholdMs: ONE_YEAR_IN_MILLISECONDS / 128,
      resolution: { windowUnit: TimeUnit.Minute, windowSize: 12 },
      description: '2+ days: 12-minute windows',
    },
    {
      thresholdMs: ONE_YEAR_IN_MILLISECONDS / 256,
      resolution: { windowUnit: TimeUnit.Minute, windowSize: 12 },
      description: '1+ day: 12-minute windows',
    },
    {
      thresholdMs: ONE_YEAR_IN_MILLISECONDS / 512,
      resolution: { windowUnit: TimeUnit.Minute, windowSize: 8 },
      description: '17+ hours: 8-minute windows',
    },
    {
      thresholdMs: ONE_YEAR_IN_MILLISECONDS / 1024,
      resolution: { windowUnit: TimeUnit.Minute, windowSize: 4 },
      description: '8+ hours: 4-minute windows',
    },
    {
      thresholdMs: ONE_YEAR_IN_MILLISECONDS / 2048,
      resolution: { windowUnit: TimeUnit.Minute, windowSize: 2 },
      description: '4+ hours: 2-minute windows',
    },
    {
      thresholdMs: ONE_YEAR_IN_MILLISECONDS / 4096,
      resolution: { windowUnit: TimeUnit.Minute, windowSize: 1 },
      description: '2+ hours: 1-minute windows',
    },
    {
      thresholdMs: ONE_YEAR_IN_MILLISECONDS / 8192,
      resolution: { windowUnit: TimeUnit.Second, windowSize: 20 },
      description: '1+ hour: 20-second windows',
    },
    {
      thresholdMs: ONE_YEAR_IN_MILLISECONDS / 16384,
      resolution: { windowUnit: TimeUnit.Second, windowSize: 12 },
      description: '30+ minutes: 12-second windows',
    },
    {
      thresholdMs: ONE_YEAR_IN_MILLISECONDS / 32000,
      resolution: { windowUnit: TimeUnit.Second, windowSize: 4 },
      description: '16+ minutes: 4-second windows',
    },
    {
      thresholdMs: TEN_MINUTES,
      resolution: { windowUnit: TimeUnit.Millisecond, windowSize: 1 },
      description: 'Small durations: 1ms windows',
    },
  ];

  /**
   * Initializes the duration-to-resolution mapping
   * This method configures the thresholds and corresponding optimal resolutions
   */
  private setup(): void {
    SmartTimeWindow.RESOLUTION_CONFIG.forEach(({ thresholdMs, resolution }) => {
      this.diffMap[thresholdMs] = resolution;
    });
  }
  /**
   * Cached sorted threshold keys for performance
   */
  private readonly sortedThresholdKeys?: number[];

  /**
   * Gets the sorted threshold keys, caching them for performance
   * @returns Array of threshold keys sorted in descending order
   */
  private getSortedThresholdKeys(): number[] {
    return (
      this.sortedThresholdKeys ??
      Object.keys(this.diffMap)
        .map(Number)
        .sort((a, b) => b - a)
    );
  }

  /**
   * Applies the resolution multiplier to a resolution, ensuring the result is valid
   * @param baseResolution - The base resolution to multiply
   * @returns The adjusted resolution with the multiplier applied
   */
  private applyMultiplier(baseResolution: Resolution): Resolution {
    const multipliedSize = Math.max(1, Math.round(baseResolution.windowSize * this.resolutionMultiplier));

    // If the multiplied size becomes very large, consider moving to a larger time unit
    if (multipliedSize >= 60) {
      switch (baseResolution.windowUnit) {
        case TimeUnit.Second:
          if (multipliedSize >= 3600) {
            return {
              windowUnit: TimeUnit.Hour,
              windowSize: Math.round(multipliedSize / 3600),
            };
          } else if (multipliedSize >= 60) {
            return {
              windowUnit: TimeUnit.Minute,
              windowSize: Math.round(multipliedSize / 60),
            };
          }
          break;
        case TimeUnit.Minute:
          if (multipliedSize >= 1440) {
            return {
              windowUnit: TimeUnit.Day,
              windowSize: Math.round(multipliedSize / 1440),
            };
          } else if (multipliedSize >= 60) {
            return {
              windowUnit: TimeUnit.Hour,
              windowSize: Math.round(multipliedSize / 60),
            };
          }
          break;
        case TimeUnit.Hour:
          if (multipliedSize >= 24) {
            return {
              windowUnit: TimeUnit.Day,
              windowSize: Math.round(multipliedSize / 24),
            };
          }
          break;
        default:
          break;
      }
    }

    return {
      windowUnit: baseResolution.windowUnit,
      windowSize: multipliedSize,
    };
  }

  /**
   * Determines the optimal time window resolution based on the configured duration
   *
   * @returns The resolution configuration that best matches the duration
   *
   * The method finds the largest threshold that is less than or equal to the duration.
   * If no threshold matches, it returns either a default 1-hour resolution (for zero duration)
   * or 1-millisecond resolution (for very small durations).
   * The resolution is then adjusted by the configured resolution multiplier.
   */
  getResolution(): Resolution {
    // Handle edge case: zero duration
    if (this.duration === 0) {
      return this.applyMultiplier({ windowUnit: TimeUnit.Hour, windowSize: 1 });
    }

    // Find the first threshold that's less than or equal to the duration
    const sortedKeys = this.getSortedThresholdKeys();
    for (const threshold of sortedKeys) {
      if (this.duration >= threshold) {
        return this.applyMultiplier(this.diffMap[threshold]);
      }
    }

    // Fallback for very small durations
    return this.applyMultiplier({
      windowUnit: TimeUnit.Millisecond,
      windowSize: 1,
    });
  }

  /**
   * Gets a human-readable description of the current resolution
   * @returns A string describing the window size and unit
   */
  getResolutionDescription(): string {
    const resolution = this.getResolution();
    const unitName = resolution.windowUnit.toLowerCase();
    const plural = resolution.windowSize > 1 ? 's' : '';
    return `${resolution.windowSize} ${unitName}${plural}`;
  }

  /**
   * Checks if the current duration would result in a specific time unit
   * @param timeUnit - The time unit to check for
   * @returns True if the resolution uses the specified time unit
   */
  usesTimeUnit(timeUnit: TimeUnit): boolean {
    return this.getResolution().windowUnit === timeUnit;
  }

  // Static factory methods for common durations

  /**
   * Creates a SmartTimeWindow for an hour duration
   * @param resolutionMultiplier - Optional multiplier for the resolution
   * @returns SmartTimeWindow instance configured for 1 hour
   */
  static forHour(resolutionMultiplier?: number): SmartTimeWindow {
    return new SmartTimeWindow(ONE_HOUR_IN_MILLISECONDS, resolutionMultiplier);
  }

  /**
   * Creates a SmartTimeWindow for a day duration
   * @param resolutionMultiplier - Optional multiplier for the resolution
   * @returns SmartTimeWindow instance configured for 1 day
   */
  static forDay(resolutionMultiplier?: number): SmartTimeWindow {
    return new SmartTimeWindow(ONE_DAY_IN_MILLISECONDS, resolutionMultiplier);
  }

  /**
   * Creates a SmartTimeWindow for a week duration
   * @param resolutionMultiplier - Optional multiplier for the resolution
   * @returns SmartTimeWindow instance configured for 7 days
   */
  static forWeek(resolutionMultiplier?: number): SmartTimeWindow {
    return new SmartTimeWindow(ONE_DAY_IN_MILLISECONDS * 7, resolutionMultiplier);
  }

  /**
   * Creates a SmartTimeWindow for a month duration (30 days)
   * @param resolutionMultiplier - Optional multiplier for the resolution
   * @returns SmartTimeWindow instance configured for 30 days
   */
  static forMonth(resolutionMultiplier?: number): SmartTimeWindow {
    return new SmartTimeWindow(ONE_DAY_IN_MILLISECONDS * 30, resolutionMultiplier);
  }

  /**
   * Creates a SmartTimeWindow for a year duration
   * @param resolutionMultiplier - Optional multiplier for the resolution
   * @returns SmartTimeWindow instance configured for 1 year
   */
  static forYear(resolutionMultiplier?: number): SmartTimeWindow {
    return new SmartTimeWindow(ONE_YEAR_IN_MILLISECONDS, resolutionMultiplier);
  }

  /**
   * Creates a SmartTimeWindow from a duration in a specific unit
   * @param value - The numeric value
   * @param unit - The time unit
   * @param resolutionMultiplier - Optional multiplier for the resolution
   * @returns SmartTimeWindow instance configured for the specified duration
   */
  static fromDuration(value: number, unit: TimeUnit, resolutionMultiplier?: number): SmartTimeWindow {
    let durationMs: number;

    switch (unit) {
      case TimeUnit.Millisecond:
        durationMs = value;
        break;
      case TimeUnit.Second:
        durationMs = value * ONE_SECOND_IN_MILLISECONDS;
        break;
      case TimeUnit.Minute:
        durationMs = value * ONE_MINUTE_IN_MILLISECONDS;
        break;
      case TimeUnit.Hour:
        durationMs = value * ONE_HOUR_IN_MILLISECONDS;
        break;
      case TimeUnit.Day:
        durationMs = value * ONE_DAY_IN_MILLISECONDS;
        break;
      case TimeUnit.Year:
        durationMs = value * ONE_YEAR_IN_MILLISECONDS;
        break;
      default:
        throw new Error(`Unsupported time unit: ${unit}`);
    }

    return new SmartTimeWindow(durationMs, resolutionMultiplier);
  }
}
